import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Sparkles } from "lucide-react";

import { VideoRecorder } from "@/features/video/VideoRecorder";
import { triggerProfileGeneration, uploadMeetupVideo } from "@/features/video/upload";
import { updateVideoPath } from "@/lib/api/participants";
import { getParticipantSession } from "@/lib/participant-session";

import { Card } from "@/components/ui/Card";
import { InlineAlert } from "@/components/ui/States";
import { MobileFrame } from "@/components/ui/MobileFrame";

const PITCH_HINTS = [
  "Who you are and what you do",
  "Your current focus or challenge",
  "How you can help others — this is what surfaces you in other people's feeds",
] as const;

export function PitchPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const session = slug ? getParticipantSession(slug) : null;

  useEffect(() => {
    if (!slug) return;
    if (!session) navigate(`/e/${slug}`, { replace: true });
  }, [slug, session, navigate]);

  const [savingProgress, setSavingProgress] = useState<number | null>(null);
  const [savingError, setSavingError] = useState<string | null>(null);

  if (!session) return null;

  async function handleSave(blob: Blob) {
    if (!session || !slug) return;
    setSavingProgress(0);
    setSavingError(null);
    try {
      const { path } = await uploadMeetupVideo(session.eventId, blob, {
        onProgress(loaded, total) {
          if (total > 0) setSavingProgress(loaded / total);
        },
      });
      // Storage write done — keep the overlay visible while we link the
      // path on the participant row + kick the AI pipeline so the user
      // never sees a flash of the recorder UI again.
      setSavingProgress(1);
      await updateVideoPath(session.participantId, path);
      void triggerProfileGeneration(session.participantId);
      // Land on the Adizes-read screen — TZ block B item 6: the read
      // is the reward for recording, shown right after upload and
      // re-accessible from the participant profile later.
      navigate(`/e/${slug}/pitch/analysis`);
    } catch (err) {
      setSavingError(err instanceof Error ? err.message : "Couldn't save your pitch. Try again.");
      setSavingProgress(null);
    }
  }

  return (
    <MobileFrame>
      <div className="flex-1 px-4 py-6 animate-fade-in">
        <h1 className="text-[24px] font-bold tracking-tight text-[#1a1a1a]">
          Now record your pitch
        </h1>
        <p className="mt-2 text-[14px] text-[#6c757d]">
          30–60 seconds, recorded as a circle video. Three points to cover:
        </p>

        <ol className="mt-5 flex flex-col gap-3">
          {PITCH_HINTS.map((text, i) => (
            <li key={text} className="flex items-start gap-3">
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#e6f1fb] text-[13px] font-bold text-[#2563eb]">
                {i + 1}
              </span>
              <span className="text-[14px] text-[#475569] leading-relaxed">
                {i === 2 ? <strong className="text-[#1a1a1a]">{text}</strong> : text}
              </span>
            </li>
          ))}
        </ol>

        <InlineAlert tone="warning" className="mt-5">
          Keep it conversational — imagine you're introducing yourself in person at the venue.
        </InlineAlert>

        {/* TZ block B promise card. The Adizes read is the carrot:
            record, and get a read of your delivery. We frame it as
            an observation, not advice, so the reader is curious
            rather than defensive when they land on the result. */}
        <Card tone="info" padding="md" className="mt-3">
          <div className="flex items-start gap-3">
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white text-[#2563eb]">
              <Sparkles className="h-4.5 w-4.5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-bold text-[#1d4ed8]">
                You'll get an Adizes read of your delivery
              </p>
              <p className="mt-1 text-[12px] leading-relaxed text-[#1e3a8a]">
                As soon as you finish recording, we'll show you how your pitch landed —
                Producer / Administrator / Entrepreneur / Integrator — anchored in your own
                phrases. Not a personality verdict, just a read of the delivery.
              </p>
            </div>
          </div>
        </Card>

        {savingError ? (
          <InlineAlert tone="danger" className="mt-3">
            {savingError}
          </InlineAlert>
        ) : null}

        <div className="mt-6">
          <VideoRecorder onSave={handleSave} />
        </div>
      </div>

      {savingProgress !== null ? <SavingOverlay progress={savingProgress} /> : null}
    </MobileFrame>
  );
}

/**
 * Full-screen overlay shown while the recorded pitch travels to Supabase
 * Storage. Hides the recorder UI immediately on Stop so the participant
 * doesn't see the "Start recording" button pop back during the upload.
 * Two phases:
 *   - progress < 1 : "Saving your pitch…" + a real progress bar
 *   - progress = 1 : "Generating your profile…" while we link the path
 *                    and kick the AI pipeline before navigating away
 */
function SavingOverlay({ progress }: { progress: number }) {
  const pct = Math.max(0, Math.min(1, progress));
  const finalising = pct >= 1;
  const headline = finalising ? "Generating your profile…" : "Saving your pitch…";
  const subline = finalising
    ? "Just a moment — almost done."
    : "Don't close the page.";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-white/95 px-6 backdrop-blur"
      aria-live="polite"
    >
      <div className="w-full max-w-[320px] text-center">
        <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#2563eb]" strokeWidth={2} />
        <h2 className="mt-5 text-[18px] font-bold tracking-tight text-[#1a1a1a]">{headline}</h2>
        <p className="mt-1 text-[13px] text-[#6c757d]">{subline}</p>
        <div className="mt-5 h-1.5 w-full overflow-hidden rounded-full bg-[#e2e8f0]">
          <div
            className="h-full rounded-full bg-[#2563eb] transition-[width] duration-200"
            style={{ width: `${Math.round(pct * 100)}%` }}
          />
        </div>
        <p className="mt-2 text-[11px] tabular-nums text-[#94a3b8]">
          {finalising ? "100%" : `${Math.round(pct * 100)}%`}
        </p>
      </div>
    </div>
  );
}
